/**
 * ポリシーテンプレート — よく使う条件式に名前を付けて置いておく場所。
 *
 * **ポリシーの判定そのものには効かない**(ポリシーの条件式にコピーして使う)。
 * 文法の検証はサーバー(`POST /api/idp/policy/check`)に聞く。
 */
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { PolicyTemplate } from "../../lib/api";
import { jst } from "../../lib/format";
import { errorText, useIdpPerms } from "./hooks";
import { AtomHelp, ExprField } from "./ExprField";
import {
  useCreatePolicyTemplate,
  useDeletePolicyTemplate,
  usePolicyTemplates,
  useUpdatePolicyTemplate,
} from "./policyHooks";

const { Text, Paragraph } = Typography;

type Values = { key: string; displayName?: string; expression: string };

function EditModal({
  row,
  open,
  onClose,
}: {
  row: PolicyTemplate | null;
  open: boolean;
  onClose: () => void;
}) {
  const [form] = Form.useForm<Values>();
  const create = useCreatePolicyTemplate();
  const update = useUpdatePolicyTemplate();
  const expr = Form.useWatch("expression", form) ?? "";

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      key: row?.key ?? "",
      displayName: row?.displayName ?? "",
      expression: row?.expression ?? "always",
    });
  }, [open, row, form]);

  const submit = (v: Values) => {
    const data = {
      key: v.key,
      displayName: v.displayName ?? "",
      expression: v.expression,
    };
    const done = { onSuccess: onClose };
    if (row) update.mutate({ id: row.id, data }, done);
    else create.mutate(data, done);
  };

  return (
    <Modal
      open={open}
      title={row ? `${row.key} を編集` : "テンプレートの追加"}
      onCancel={onClose}
      onOk={() => void form.submit()}
      okText={row ? "保存する" : "追加する"}
      cancelText="やめる"
      confirmLoading={create.isPending || update.isPending}
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="key"
          label="キー(半角英数と - _ のみ)"
          rules={[{ required: true, message: "キーは必須" }]}
        >
          <Input maxLength={64} placeholder="office-only" />
        </Form.Item>
        <Form.Item name="displayName" label="表示名">
          <Input maxLength={100} placeholder="社内からのみ" />
        </Form.Item>
        <ExprField
          name="expression"
          label="条件式"
          value={expr}
          extra="ポリシーの条件欄にそのまま貼れる形で書く。"
        />
      </Form>
    </Modal>
  );
}

export function PolicyTemplatesPage() {
  const { canAdmin } = useIdpPerms();
  const query = usePolicyTemplates();
  const remove = useDeletePolicyTemplate();
  const [editing, setEditing] = useState<PolicyTemplate | null>(null);
  const [open, setOpen] = useState(false);

  const columns: ColumnsType<PolicyTemplate> = [
    { title: "キー", width: 160, dataIndex: "key", render: (v: string) => <Text code>{v}</Text> },
    { title: "表示名", width: 160, dataIndex: "displayName" },
    {
      title: "条件式",
      width: 400,
      ellipsis: true,
      onCell: (row) => ({ title: String(row.expression ?? "") }),
      dataIndex: "expression",
      render: (v: string) => (
        <Space>
          <Text code>{v}</Text>
          <Button
            size="small"
            type="link"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(v)
                .then(() => message.success("条件式をコピーした"))
                .catch(() => message.warning("コピーできなかった(手で選んでコピーする)"));
            }}
          >
            コピー
          </Button>
        </Space>
      ),
    },
    {
      title: "使われている数",
      dataIndex: "usedCount",
      width: 140,
      render: (n: number) =>
        n > 0 ? <Tag color="blue">{n} 箇所</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: "更新",
      dataIndex: "updatedAt",
      width: 180,
      render: (v: string | null) => jst(v) || "—",
    },
    {
      title: "",
      key: "op",
      width: 180,
      render: (_: unknown, row) =>
        canAdmin && (
          <Space>
            <Button
              size="small"
              onClick={() => {
                setEditing(row);
                setOpen(true);
              }}
            >
              編集
            </Button>
            <Button
              size="small"
              danger
              onClick={() =>
                Modal.confirm({
                  title: `${row.key} を削除する`,
                  content:
                    "テンプレートを消しても、同じ条件式を使っているポリシーはそのまま効く。",
                  okText: "削除する",
                  okButtonProps: { danger: true },
                  cancelText: "やめる",
                  onOk: () => remove.mutate(row.id),
                })
              }
            >
              削除
            </Button>
          </Space>
        ),
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          ポリシーテンプレート
        </Typography.Title>
        <Space>
          <AtomHelp />
          {canAdmin && (
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
            >
              テンプレートの追加
            </Button>
          )}
        </Space>
      </Flex>

      <Paragraph type="secondary">
        よく使う条件式の置き場。<Text strong>判定に直接は効かない</Text>
        (ポリシーの条件欄にコピーして使う)。式が読めるかどうかは入力中に検証する。
      </Paragraph>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="テンプレートを取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        <Table<PolicyTemplate>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          loading={query.isFetching || remove.isPending}
          columns={columns}
          dataSource={query.data?.items ?? []}
          pagination={false}
          locale={{ emptyText: "テンプレートはまだ無い" }}
        />
      </Card>

      <EditModal row={editing} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
